import { useState, useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Checkbox,
  useDisclosure,
  Textarea,
} from '@nextui-org/react';
import { toast } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import CategorySelector from '../../components/CategorySelector';
import {
  createAskersCategoryTheme,
  deleteAskersCategoryTheme,
  fetchAskersCategoryThemes,
  updateAskersCategoryTheme,
} from 'common/src/api/askers_category_themes';
import { fetchAskerCategoriesTheme, uploadAskerCategoriesTheme } from 'common/src/api/storage/asker-categories-themes';

interface CategoryTheme {
  id: number | null;
  title: string | null;
  description: string | null;
  categories_ids: number[];
  is_active: boolean;
  image_url: string | null;
  is_featured: boolean;
  is_featured_seasonal: boolean;
}

const emptyTheme = {
  id: null,
  title: '',
  description: '',
  image_url: null,
  is_active: true,
  is_featured: false,
  categories_ids: [],
  is_featured_seasonal: false,
};

export default function CategoryTopics() {
  const [themes, setThemes] = useState<CategoryTheme[]>([]);
  const [currentTheme, setCurrentTheme] = useState(emptyTheme);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fetchThemes = async () => {
    console.log('Fetching themes...');
    const { data, error } = await fetchAskersCategoryThemes();
    console.log('Fetch response:', { data, error });

    if (error) {
      console.error('Error fetching themes:', error);
      toast.error(`Error al cargar los temas: ${error.message}`);
      return;
    }

    if (!data) {
      console.error('No data returned when fetching themes');
      return;
    }

    console.log('Setting themes:', data);
    setThemes(data);
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  const handleSubmit = async () => {
    try {
      if (!currentTheme.title?.trim()) {
        toast.error('El título es requerido');
        return;
      }

      const themeData = {
        title: currentTheme.title,
        description: currentTheme.description,
        image_url: currentTheme.image_url,
        is_active: currentTheme.is_active,
        is_featured: currentTheme.is_featured,
        is_featured_seasonal: currentTheme.is_featured_seasonal,
        categories_ids: currentTheme.categories_ids,
      };

      if (isEditing) {
        const { error } = await updateAskersCategoryTheme(currentTheme.id, themeData);
        if (error) throw error;
        toast.success('Tema actualizado');
      } else {
        const { error } = await createAskersCategoryTheme(themeData);

        if (error) throw error;
        toast.success('Tema creado');
      }

      fetchThemes();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este tema?')) return;

    const { error } = await deleteAskersCategoryTheme(id);

    if (error) {
      toast.error('Error al eliminar el tema');
      return;
    }

    toast.success('Tema eliminado');
    fetchThemes();
  };

  const handleEdit = (theme: CategoryTheme) => {
    console.log('Editing theme:', theme);
    setCurrentTheme(theme);
    setIsEditing(true);
    onOpen();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;

      const { error: uploadError } = await uploadAskerCategoriesTheme(fileName, file)

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = fetchAskerCategoriesTheme(fileName);

      setCurrentTheme((prev) => ({ ...prev, image_url: publicUrl }));
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error(error);
      toast.error('Error al subir la imagen');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-6">
        <h1 className="text-2xl font-bold">Temas de Categorías</h1>
        <Button
          color="primary"
          onPress={() => {
            setCurrentTheme(emptyTheme);
            setIsEditing(false);
            onOpen();
          }}
        >
          Nuevo Tema
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableColumn>ID</TableColumn>
          <TableColumn>Título</TableColumn>
          <TableColumn>Imagen</TableColumn>
          <TableColumn>Estado</TableColumn>
          <TableColumn>Destacado</TableColumn>
          <TableColumn>Destacado Temporal</TableColumn>
          <TableColumn>Acciones</TableColumn>
        </TableHeader>
        <TableBody>
          {themes.map((theme) => (
            <TableRow key={theme.id}>
              <TableCell>{theme.id}</TableCell>
              <TableCell>{theme.title}</TableCell>
              <TableCell>
                {theme.image_url ? (
                  <img
                    src={theme.image_url}
                    alt={theme.title || 'Imagen del tema'}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <span className="text-gray-400">Sin imagen</span>
                )}
              </TableCell>
              <TableCell>{theme.is_active ? 'Activo' : 'Inactivo'}</TableCell>
              <TableCell>{theme.is_featured ? 'Sí' : 'No'}</TableCell>
              <TableCell>{theme.is_featured_seasonal ? 'Sí' : 'No'}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button size="sm" onPress={() => handleEdit(theme)}>
                    Editar
                  </Button>
                  <Button size="sm" color="danger" onPress={() => handleDelete(theme.id)}>
                    Eliminar
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
        classNames={{
          base: 'max-w-[900px]',
        }}
      >
        <ModalContent>
          <ModalHeader>{isEditing ? 'Editar Tema' : 'Nuevo Tema'}</ModalHeader>
          <ModalBody>
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="flex flex-col gap-4">
                <Input
                  label="Título"
                  value={currentTheme.title ?? ''}
                  onChange={(e) => setCurrentTheme((prev) => ({ ...prev, title: e.target.value }))}
                  isRequired
                />
                <Textarea
                  label="Descripción"
                  value={currentTheme.description ?? ''}
                  onChange={(e) =>
                    setCurrentTheme((prev) => ({ ...prev, description: e.target.value }))
                  }
                  isRequired
                />
                <div>
                  <label className="text-sm block mb-2">Categorías</label>
                  <CategorySelector
                    selectedIds={currentTheme.categories_ids || []}
                    onChange={(ids) =>
                      setCurrentTheme((prev) => ({ ...prev, categories_ids: ids }))
                    }
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm">Imagen</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary file:text-white
                      hover:file:bg-primary/80"
                  />
                  {currentTheme.image_url && (
                    <img
                      src={currentTheme.image_url}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg mt-2"
                    />
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Checkbox
                    isSelected={currentTheme.is_active}
                    onValueChange={(value) =>
                      setCurrentTheme((prev) => ({ ...prev, is_active: value }))
                    }
                    aria-label="Activar tema"
                  >
                    Activo
                  </Checkbox>
                  <Checkbox
                    isSelected={currentTheme.is_featured}
                    onValueChange={(value) =>
                      setCurrentTheme((prev) => ({ ...prev, is_featured: value }))
                    }
                    aria-label="Destacar tema"
                  >
                    Destacado
                  </Checkbox>
                  <Checkbox
                    isSelected={currentTheme.is_featured_seasonal}
                    onValueChange={(value) =>
                      setCurrentTheme((prev) => ({ ...prev, is_featured_seasonal: value }))
                    }
                    aria-label="Destacar tema temporalmente"
                  >
                    De Temporada
                  </Checkbox>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose}>
              Cancelar
            </Button>
            <Button color="primary" onPress={handleSubmit} isDisabled={isUploading}>
              {isUploading ? 'Subiendo imagen...' : 'Guardar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
